/**
 * @fileoverview Admin app refresh component for managing user and facility data.
 */

import React, {FunctionComponent, PropsWithChildren} from "react";
import {connect} from "react-redux";
import {useIsFetching, useQueryClient} from "react-query";
import {UserDto} from "../utils/types/user";

import {AppState, Dispatch, select} from "../store";
import {useRefreshUserProfile} from "../hooks/useRefreshUserProfileQuery";
import {
  useFacilitiesQuery,
  FacilitiesQueryName,
} from "../hooks/useFacilitiesQuery";

// Re-export for backward compatibility
export {FacilitiesQueryName};

/**
 * Props for the component state from Redux.
 */
type StateProps = Readonly<{isLoggedIn: boolean; userId: string}>;

/**
 * Props for dispatch actions.
 */
type DispatchProps = Readonly<{
  setUser: ({
    user,
    isUserLogin,
  }: {
    user: UserDto;
    isUserLogin: boolean;
  }) => Promise<void>;
  setFacilities: (data: any) => Promise<void>;
}>;

/**
 * Custom hook for manually fetching facilities data.
 * Provides a function to trigger facilities refetch and loading state.
 *
 * @returns {Object} Object containing fetch function and loading state.
 * @returns {Function} returns.fetchFacilitiesQuery Function to fetch facilities data.
 * @returns {boolean} returns.isFetchingFacilities Whether facilities are currently being fetched.
 */
export const useFetchFacilitiesQuery = () => {
  const queryClient = useQueryClient();
  const isFetchingFacilities = useIsFetching(FacilitiesQueryName);

  return {
    fetchFacilitiesQuery: () => queryClient.fetchQuery(FacilitiesQueryName),
    isFetchingFacilities: Boolean(isFetchingFacilities),
  };
};

/**
 * Renderer component that manages user and facility data refresh.
 *
 * @param {StateProps & DispatchProps} props Component props.
 * @param {React.ReactNode} props.children Child components to render.
 * @param {Function} props.setFacilities Function to set facilities data.
 * @param {Function} props.setUser Function to set user data.
 * @param {boolean} props.isLoggedIn Whether the user is logged in.
 * @param {string} props.userId The user ID.
 * @returns {JSX.Element} The rendered component.
 */
const Renderer: FunctionComponent<
  PropsWithChildren<StateProps & DispatchProps>
> = ({children, setFacilities, setUser, isLoggedIn, userId}) => {
  useFacilitiesQuery({
    enabled: isLoggedIn,
    onSuccess: data => setFacilities(data?.content),
  });

  useRefreshUserProfile({
    userId,
    setUser: user => setUser({user, isUserLogin: false}),
  });

  return <>{children}</>;
};

/**
 * Maps Redux state to component props.
 */
const mapState = (state: AppState) => ({
  isLoggedIn: select.adminSession.isLoggedIn(state),
  userId: select.adminSession.user(state)?.id,
});

/**
 * Maps Redux dispatch actions to component props.
 */
const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  setUser: dispatch.adminSession.setUser,
  setFacilities: dispatch.adminSession.setFacilities,
});

/**
 * Connected component that refreshes admin app data.
 * Manages user profile and facilities data refresh when logged in.
 */
export const AdminAppRefresh = connect(mapState, mapDispatch)(Renderer);
